import { useEffect, useMemo, useState } from "react";
import type { LoadImage } from "./LazyImage";
import { base64Bytes, decodeDataUrl, fileLabel, humanSize } from "../attachments";
import { fencedFor, looksTextual, textPreview } from "../textPreview";
import { renderMarkdown } from "../md";

/**
 * How many bytes of a file the preview can possibly need.
 *
 * Enough that a longer file always overruns the preview's own limits, which is what makes reading only this
 * much indistinguishable from reading all of it: the shortest characters are one byte, so this many bytes is
 * more characters than a preview shows, and anything past it would have been cut anyway.
 */
const PREVIEW_BYTES = 16_384;

/**
 * An attachment that is not a picture: its name and a link that saves it.
 *
 * The bytes arrive the same way an image's do — decrypted here and handed back as an object URL — so the
 * difference is only what can be done with them. A picture is shown; a file has nothing to show, so the row
 * carries what it is called and offers to save it under that name.
 *
 * The size is counted off the decrypted bytes rather than sent with the message. It is not known until they
 * are here, and a number taken from anywhere else would be a claim about the file made by something that
 * had not seen it.
 */
export function LazyFile({
  att,
  mime,
  name,
  load,
}: {
  att: string;
  mime?: string;
  name?: string;
  load: LoadImage;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [size, setSize] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let ok = true;
    void load(att, mime ?? "application/octet-stream")
      .then((u) => {
        if (!ok) return;
        if (!u) {
          setFailed(true);
          return;
        }
        setUrl(u);
        setSize(base64Bytes(u));
      })
      .catch(() => ok && setFailed(true));
    return () => {
      ok = false;
    };
  }, [att, mime, load]);
  const label = fileLabel(name, mime);
  // A file whose contents are text is read here rather than only saved from here. The row above it keeps
  // every part it had, so saving the whole thing is still one click. The KIND is asked before the bytes are
  // touched: decoding walks the string a character at a time, and doing that to a ten megabyte archive only
  // to find out it was never text stops the tab.
  const preview = useMemo(
    () => (url && looksTextual(name, mime) ? textPreview(decodeDataUrl(url, PREVIEW_BYTES), name, mime) : null),
    [url, name, mime],
  );
  if (failed) return <span className="muted">Couldn't load {label}</span>;
  if (!url) return <span className="muted">Loading {label}…</span>;
  return (
    <div className="file-block">
      <a className="file-card" href={url} download={label}>
        <span className="file-icon">📎</span>
        <span className="file-meta">
          <span className="file-name">{label}</span>
          <span className="file-sub">{size === null ? "Save" : `${humanSize(size)} · save`}</span>
        </span>
      </a>
      {preview && (
        <>
          {/* Drawn by what already draws the chat's listings, so the highlighting and the copy control are
              the ones the reader knows from a fenced block. */}
          <div className="md" dangerouslySetInnerHTML={{ __html: renderMarkdown(fencedFor(preview)) }} />
          {preview.truncated && <span className="file-sub">Save it to read the rest</span>}
        </>
      )}
    </div>
  );
}
